/*======================================================================================================================
  * Bluebell: src/app/state/annotations/annotations.selectors.ts
  * Last Edited: 16/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { constructPostproUrl, constructThumbnailUrl } from '@app/components/postpro-viewer/postpro-viewer.service';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Annotation,
  AnnotationReference,
  AnnotationThumbnailAttitude,
} from '@state/annotations/annotations.interfaces';
import { selectAccessToken } from '@state/authentication/authentication.selectors';
import * as fromAnnotations from './annotations.reducer';

export const selectAnnotationsState = createFeatureSelector<fromAnnotations.State>(fromAnnotations.featureKey);

export const selectAllAnnotations = createSelector(
  selectAnnotationsState,
  fromAnnotations.getAll,
);

export const selectAnnotationEntities = createSelector(
  selectAnnotationsState,
  fromAnnotations.getEntities,
);

export const selectAnnotationIds = createSelector(
  selectAnnotationsState,
  fromAnnotations.getIds,
);

export const selectAnnotationCount = createSelector(
  selectAnnotationsState,
  fromAnnotations.getTotal,
);

export const selectAnnotationsLoading = createSelector(
  selectAnnotationsState,
  fromAnnotations.getLoading
);

export const selectCasesWithAnnotations = createSelector(
  selectAnnotationsState,
  fromAnnotations.getCasesWithAnnotations
);

export const selectCasesWithAnnotationsLoading = createSelector(
  selectAnnotationsState,
  fromAnnotations.getCasesWithAnnotationsLoading
);

export const selectSelectedAnnotationCases = createSelector(
  selectAnnotationsState,
  fromAnnotations.getSelectedCases
);

export const selectSelectedAnnotationIds = createSelector(
  selectAnnotationsState,
  fromAnnotations.getSelectedAnnotations
);

export const selectLoadedAnnotationCases = createSelector(
  selectAnnotationsState,
  fromAnnotations.getLoadedCases
);

export const selectAllowMultipleAnnotations = createSelector(
  selectAnnotationsState,
  fromAnnotations.getAllowMultiple
);

// Complex Selectors

export const selectAnnotationReferencesUnfiltered = createSelector(
  selectAllAnnotations,
  selectAccessToken,
  (annotations: Annotation[], token: string|null) => annotations.map((annotation: Annotation) => {
    const url = constructPostproUrl(annotation.path, token as string);
    const thumbnailUrl = constructThumbnailUrl(url);
    return { ...annotation, url, thumbnailUrl };
  })
);

export const selectAnnotationReferences = createSelector(
  selectAnnotationReferencesUnfiltered,
  selectSelectedAnnotationIds,
  (references: AnnotationReference[], uuids: string[]) =>
    references
      .filter(reference => uuids.includes(reference.uuid))
      .sort((a: AnnotationReference, b: AnnotationReference) => {
        if (a.index == null || b.index == null) {
          return 0;
        } else {
          return a.index - b.index
        }
      })
);

export const selectAnnotationThumbnailsAttitudes = createSelector(
  selectAnnotationReferencesUnfiltered,
  selectSelectedAnnotationCases,
  (annotations: AnnotationReference[], cases: string[]): AnnotationThumbnailAttitude[] => Object.values(annotations.reduce(
    (thumbnails: Record<string, AnnotationThumbnailAttitude>, annotation: AnnotationReference) => {
      // const thumbnail = { url: annotation.thumbnailUrl, name: annotation.name, uuid: annotation.uuid, index: annotation.index };
      if (cases.includes(annotation.case.uuid)) {
        if (annotation.case.uuid in thumbnails) {
          thumbnails[annotation.case.uuid].annotations = [...thumbnails[annotation.case.uuid].annotations, annotation];
        } else {
          thumbnails[annotation.case.uuid] = {
            ...annotation.case,
            annotations: [annotation]
          };
        }
      }
      return thumbnails;
    },
    {}
  ))
);
